import React, { useCallback, useState, useMemo, useEffect } from 'react';
import {
  CommandSelect,
  CommandSelectTrigger,
  CommandSelectValue,
  CommandSelectContent,
  CommandSelectItem,
  CommandSelectGroup,
  CommandSelectAll,
  CommandSelectEmpty,
  useCommandSelectSearch,
} from './command-select';
import { Project } from './types';

export interface ProjectsSelectProps {
  children: React.ReactNode;
}

export const ProjectsSelect: React.FC<ProjectsSelectProps> = ({ children }) => {
  return <div className="relative">{children}</div>;
};

export const ProjectsOptions = ({
  projects,
  isLoading: _isLoading,
  value,
  onChange,
}: {
  projects: Project[];
  isLoading: boolean;
  value: number[];
  onChange: (ids: number[]) => void;
}) => {
  const [selectedProjectOptions, setSelectedProjectOptions] = useState<number[]>(value || []);
  const [isUpdating, setIsUpdating] = useState(false);

  // Sync with form values when not updating
  useEffect(() => {
    if (!isUpdating) {
      // Only update if arrays are actually different (deep equality check)
      const arraysAreEqual = selectedProjectOptions.length === (value || []).length &&
        selectedProjectOptions.every((id, index) => id === (value || [])[index]);

      if (!arraysAreEqual) {
        setSelectedProjectOptions(value || []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isUpdating]); // selectedProjectOptions intentionally excluded to prevent infinite loop

  // Unified update handler with proper state management
  const updateSelection = useCallback(
    (newSelection: number[]) => {
      setIsUpdating(true);
      setSelectedProjectOptions(newSelection);
      onChange(newSelection);

      // Reset updating flag after state has settled
      setTimeout(() => {
        setIsUpdating(false);
      }, 0);
    },
    [onChange],
  );

  const handleDismiss = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      updateSelection([]);
    },
    [updateSelection],
  );

  const getDisplayText = () => {
    if (selectedProjectOptions.length === 0) return { mainText: 'Select projects...', count: null };
    if (selectedProjectOptions.length === 1) {
      const project = projects.find((p) => p.id === selectedProjectOptions[0]);
      return { mainText: project ? project.name : 'Select projects...', count: null };
    }
    const firstProject = projects.find((p) => p.id === selectedProjectOptions[0]);
    return {
      mainText: firstProject?.name || 'Select projects...',
      count: selectedProjectOptions.length - 1
    };
  };

  return (
    <CommandSelect>
      <CommandSelectTrigger placeholder="Select projects..." data-dd-action-name="social-analytics-project-select-trigger">
        <CommandSelectValue
          placeholder="Select projects..."
          displayText={selectedProjectOptions.length > 0 ? getDisplayText().mainText : undefined}
          count={getDisplayText().count}
          onClear={handleDismiss}
          disabled={isUpdating}
          data-dd-action-name="social-analytics-project-select-clear"
        />
      </CommandSelectTrigger>
      <CommandSelectContent searchPlaceholder="Search projects..." maxHeight="200px" useWrapper={false}>
        <ProjectsContentWithSearch
          projects={projects}
          selectedProjectOptions={selectedProjectOptions}
          isUpdating={isUpdating}
          updateSelection={updateSelection}
        />
      </CommandSelectContent>
    </CommandSelect>
  );
};

// Component that uses the search context for filtering and grouping
const ProjectsContentWithSearch = ({
  projects,
  selectedProjectOptions,
  isUpdating,
  updateSelection,
}: {
  projects: Project[];
  selectedProjectOptions: number[];
  isUpdating: boolean;
  updateSelection: (newSelection: number[]) => void;
}) => {
  // Get the search query from the CommandSelect context
  const searchQuery = useCommandSelectSearch();

  // Split projects into active and inactive
  const activeProjects = useMemo(() => projects.filter((p) => p.isActive), [projects]);
  const inactiveProjects = useMemo(() => projects.filter((p) => !p.isActive), [projects]);

  // Filter projects based on search query
  const filterProjects = useCallback((projectList: Project[]) => {
    if (!searchQuery) return projectList;
    const query = searchQuery.toLowerCase();
    return projectList.filter((project) => project.name.toLowerCase().includes(query));
  }, [searchQuery]);

  const filteredActiveProjects = useMemo(() => filterProjects(activeProjects), [activeProjects, filterProjects]);
  const filteredInactiveProjects = useMemo(() => filterProjects(inactiveProjects), [inactiveProjects, filterProjects]);

  // Calculate select all state for Active Projects section
  const getActiveSelectAllState = useMemo(() => {
    const activeProjectIds = filteredActiveProjects.map((p) => p.id);
    const selectedActiveIds = selectedProjectOptions.filter((id) => activeProjectIds.includes(id));

    return {
      checked: activeProjectIds.length > 0 && selectedActiveIds.length === activeProjectIds.length,
      indeterminate: selectedActiveIds.length > 0 && selectedActiveIds.length < activeProjectIds.length,
      disabled: activeProjectIds.length === 0 || isUpdating
    };
  }, [filteredActiveProjects, selectedProjectOptions, isUpdating]);

  // Calculate select all state for Archived Projects section
  const getArchivedSelectAllState = useMemo(() => {
    const archivedProjectIds = filteredInactiveProjects.map((p) => p.id);
    const selectedArchivedIds = selectedProjectOptions.filter((id) => archivedProjectIds.includes(id));

    return {
      checked: archivedProjectIds.length > 0 && selectedArchivedIds.length === archivedProjectIds.length,
      indeterminate: selectedArchivedIds.length > 0 && selectedArchivedIds.length < archivedProjectIds.length,
      disabled: archivedProjectIds.length === 0 || isUpdating
    };
  }, [filteredInactiveProjects, selectedProjectOptions, isUpdating]);

  // Handle select all/deselect all for Active Projects section
  const handleActiveSelectAllToggle = useCallback(() => {
    if (isUpdating) return;

    const activeProjectIds = filteredActiveProjects.map((p) => p.id);
    const selectedActiveIds = selectedProjectOptions.filter((id) => activeProjectIds.includes(id));

    let newSelection: number[];

    if (selectedActiveIds.length === activeProjectIds.length) {
      // Deselect all active projects (preserve other selections)
      newSelection = selectedProjectOptions.filter((id) => !activeProjectIds.includes(id));
    } else {
      // Select all active projects (preserve existing selections)
      newSelection = Array.from(new Set([...selectedProjectOptions, ...activeProjectIds]));
    }

    updateSelection(newSelection);
  }, [filteredActiveProjects, selectedProjectOptions, updateSelection, isUpdating]);

  // Handle select all/deselect all for Archived Projects section
  const handleArchivedSelectAllToggle = useCallback(() => {
    if (isUpdating) return;

    const archivedProjectIds = filteredInactiveProjects.map((p) => p.id);
    const selectedArchivedIds = selectedProjectOptions.filter((id) => archivedProjectIds.includes(id));

    let newSelection: number[];

    if (selectedArchivedIds.length === archivedProjectIds.length) {
      // Deselect all archived projects (preserve other selections)
      newSelection = selectedProjectOptions.filter((id) => !archivedProjectIds.includes(id));
    } else {
      // Select all archived projects (preserve existing selections)
      newSelection = Array.from(new Set([...selectedProjectOptions, ...archivedProjectIds]));
    }

    updateSelection(newSelection);
  }, [filteredInactiveProjects, selectedProjectOptions, updateSelection, isUpdating]);

  const handleSelectProject = useCallback(
    (projectId: number) => {
      if (isUpdating) return;

      const newValue = selectedProjectOptions.includes(projectId)
        ? selectedProjectOptions.filter((v) => v !== projectId)
        : [...selectedProjectOptions, projectId];

      updateSelection(newValue);
    },
    [selectedProjectOptions, updateSelection, isUpdating],
  );

  const hasAnyResults = filteredActiveProjects.length > 0 || filteredInactiveProjects.length > 0;
  const hasAnyProjects = projects.length > 0;

  return (
    <>
      {hasAnyResults ? (
        <>
          {filteredActiveProjects.length > 0 && (
            <CommandSelectGroup heading="Active Projects">
              <CommandSelectAll
                checked={getActiveSelectAllState.checked}
                indeterminate={getActiveSelectAllState.indeterminate}
                disabled={getActiveSelectAllState.disabled}
                onToggle={handleActiveSelectAllToggle}
                count={filteredActiveProjects.length}
                data-dd-action-name="social-analytics-project-select-all-active"
              />
              {filteredActiveProjects.map((project) => (
                <CommandSelectItem
                  key={project.id}
                  value={String(project.id)}
                  onSelect={() => handleSelectProject(project.id)}
                  selected={selectedProjectOptions.includes(project.id)}
                  disabled={isUpdating}
                  data-dd-action-name="social-analytics-project-select"
                >
                  <span>{project.name}</span>
                </CommandSelectItem>
              ))}
            </CommandSelectGroup>
          )}
          {filteredInactiveProjects.length > 0 && (
            <CommandSelectGroup heading="Archived Projects">
              <CommandSelectAll
                checked={getArchivedSelectAllState.checked}
                indeterminate={getArchivedSelectAllState.indeterminate}
                disabled={getArchivedSelectAllState.disabled}
                onToggle={handleArchivedSelectAllToggle}
                count={filteredInactiveProjects.length}
                data-dd-action-name="social-analytics-project-select-all-archived"
              />
              {filteredInactiveProjects.map((project) => (
                <CommandSelectItem
                  key={project.id}
                  value={String(project.id)}
                  onSelect={() => handleSelectProject(project.id)}
                  selected={selectedProjectOptions.includes(project.id)}
                  disabled={isUpdating}
                  data-dd-action-name="social-analytics-project-select"
                >
                  <span>{project.name}</span>
                </CommandSelectItem>
              ))}
            </CommandSelectGroup>
          )}
        </>
      ) : (
        <CommandSelectEmpty>
          {hasAnyProjects ? 'No projects found.' : 'No projects available.'}
        </CommandSelectEmpty>
      )}
    </>
  );
};
